import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithCustomToken,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  deleteDoc,
  collection, 
  onSnapshot,
  writeBatch,
  serverTimestamp   // FIX: use server timestamps instead of client-side new Date()
} from 'firebase/firestore';
import { 
  Settings, 
  LogOut, 
  Plus, 
  Minus, 
  ChevronLeft, 
  ChevronRight,
  Beer,
  Trash2,
  TableProperties,
  Calendar as CalendarIcon,
  Search,
  LayoutGrid,
  TrendingUp,
  TrendingDown,
  BarChart3,
  CalendarDays,
} from 'lucide-react';

// --- Firebase Configuration ---
// FIX: Credentials pulled from environment variables, not hardcoded in source.
// Create a .env file in your project root with these keys (see .env.example).
const firebaseConfig = typeof window !== 'undefined' && window.__firebase_config 
  ? JSON.parse(window.__firebase_config) 
  : {
      apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId:             process.env.REACT_APP_FIREBASE_APP_ID,
    };

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof window !== 'undefined' && window.__app_id ? window.__app_id : 'drink-tracker-v2';

const DEFAULT_THRESHOLDS = [
  { max: 0,  color: '#22c55e', label: 'None'     },
  { max: 2,  color: '#eab308', label: 'Moderate' },
  { max: 4,  color: '#f97316', label: 'High'     },
  { max: 99, color: '#ef4444', label: 'Heavy'    }
];

const TIMEZONES = Intl.supportedValuesOf('timeZone');

// ---------------------------------------------------------------------------
// FIX: Shared utility — was duplicated inside App and AdvancedView components.
// ---------------------------------------------------------------------------
function getColorForCount(count, thresholds) {
  if (count === undefined || count === null) return 'transparent';
  const sorted = [...thresholds].sort((a, b) => a.max - b.max);
  for (const t of sorted) if (count <= t.max) return t.color;
  return sorted[sorted.length - 1]?.color || 'transparent';
}

// ---------------------------------------------------------------------------
// FIX: Timezone-safe date string builder (replaces toISOString() in the grid).
// toISOString() is UTC-based and can produce the wrong date for users whose
// local timezone is behind UTC (e.g. UTC-5 at 11 PM would return tomorrow).
// ---------------------------------------------------------------------------
function buildDateStr(year, month1indexed, day) {
  return `${year}-${String(month1indexed).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function App() {
  const [user, setUser]               = useState(null);
  const [view, setView]               = useState('loading');
  const [logs, setLogs]               = useState({});
  const [thresholds, setThresholds]   = useState(DEFAULT_THRESHOLDS);
  const [timezone, setTimezone]       = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [authError, setAuthError]     = useState('');
  const [loading, setLoading]         = useState(true);
  // FIX: Surface write errors to the user instead of silently swallowing them.
  const [writeError, setWriteError]   = useState('');

  const getTodayStr = () => {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  useEffect(() => {
    // onAuthStateChanged is the single source of truth for auth state.
    // It fires once immediately with the current user (or null), so we use
    // it to both initialise state AND drive loading — no race condition.
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Already signed in (e.g. page refresh with existing session).
        setUser(u);
        setView('calendar');
        setLoading(false);
      } else {
        // Not signed in — try custom token from host environment first.
        const token = typeof window !== 'undefined' ? window.__initial_auth_token : null;
        if (token) {
          try {
            await signInWithCustomToken(auth, token);
            // onAuthStateChanged will fire again with the new user — no need to
            // call setUser/setView/setLoading here; they run in the next callback.
          } catch (e) {
            console.error('Custom token sign-in failed:', e);
            setLoading(false);
          }
        } else {
          // No custom token and no existing session — show the login screen.
          setUser(null);
          setView('login');
          setLoading(false);
        }
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) return;

    const logsRef = collection(db, 'artifacts', appId, 'users', user.uid, 'daily_logs');
    const unsubLogs = onSnapshot(logsRef, (snapshot) => {
      const data = {};
      snapshot.forEach(docSnap => { data[docSnap.id] = docSnap.data().count; });
      setLogs({ ...data });
    });

    const settingsRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'prefs');
    const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.thresholds) setThresholds(data.thresholds);
        if (data.timezone)   setTimezone(data.timezone);
      }
    });

    return () => { unsubLogs(); unsubSettings(); };
  }, [user]);

  // FIX: Input validation + serverTimestamp + error surfacing.
  const updateLog = async (dateStr, count) => {
    if (!user) return;
    setWriteError('');

    // Validate count before touching Firestore
    if (count !== null) {
      if (typeof count !== 'number' || isNaN(count) || count < 0 || count > 100) {
        setWriteError('Invalid count. Please enter a number between 0 and 100.');
        return;
      }
    }

    const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', dateStr);
    try {
      if (count === null) {
        await deleteDoc(ref);
      } else {
        await setDoc(ref, {
          count,
          updatedAt: serverTimestamp()   // FIX: server-side timestamp, not client clock
        });
      }
    } catch (e) {
      console.error('Write failed:', e);
      setWriteError('Failed to save. Please check your connection and try again.');
    }
  };

  const getMonthStats = (year, month) => {
    let total = 0, daysWithLogs = 0;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = buildDateStr(year, month + 1, d);
      if (logs[dateStr] !== undefined) { total += logs[dateStr]; daysWithLogs++; }
    }
    return {
      total,
      dailyAvg:  daysWithLogs > 0 ? total / daysWithLogs       : 0,
      weeklyAvg: daysWithLogs > 0 ? (total / daysWithLogs) * 7 : 0
    };
  };

  const getYearStats = (year) => {
    let total = 0, daysWithLogs = 0;
    const monthsWithLogs = new Set();
    Object.entries(logs).forEach(([dateStr, count]) => {
      if (dateStr.startsWith(`${year}-`)) {
        total += count; daysWithLogs++;
        monthsWithLogs.add(dateStr.substring(0, 7));
      }
    });
    return {
      total,
      dailyAvg:   daysWithLogs > 0       ? total / daysWithLogs         : 0,
      weeklyAvg:  daysWithLogs > 0       ? (total / daysWithLogs) * 7   : 0,
      monthlyAvg: monthsWithLogs.size > 0 ? total / monthsWithLogs.size : 0
    };
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-950">
      <Beer className="animate-bounce text-amber-500 w-12 h-12" />
    </div>
  );

  if (view === 'login') return (
    <LoginView
      onAuth={(e, p, s) => {
        setAuthError('');
        const action = s ? createUserWithEmailAndPassword : signInWithEmailAndPassword;
        action(auth, e, p).catch(err => setAuthError(err.message));
      }}
      error={authError}
    />
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 pb-20">
      <nav className="p-4 border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex justify-between items-center sticky top-0 z-50 border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2 font-black text-xl">
          <div className="bg-amber-500 p-1.5 rounded-lg shadow-lg"><Beer className="text-white" size={20} /></div>
          <span className="hidden sm:inline">Tracker</span>
        </div>
        <div className="flex gap-1 sm:gap-2">
          <NavBtn icon={<CalendarIcon size={20}/>} active={view === 'calendar'}  onClick={() => setView('calendar')}  />
          <NavBtn icon={<LayoutGrid size={20}/>}   active={view === 'year'}      onClick={() => setView('year')}      />
          <NavBtn icon={<TableProperties size={20}/>} active={view === 'advanced'} onClick={() => setView('advanced')} />
          <NavBtn icon={<Settings size={20}/>}     active={view === 'settings'}  onClick={() => setView('settings')}  />
          <button onClick={() => signOut(auth)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl transition-colors">
            <LogOut size={20} />
          </button>
        </div>
      </nav>

      {/* FIX: Global write-error banner */}
      {writeError && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-bold rounded-2xl flex justify-between items-center">
          <span>{writeError}</span>
          <button onClick={() => setWriteError('')} className="ml-4 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <main className="max-w-6xl mx-auto p-4 md:p-8">

        {/* ── Calendar View ─────────────────────────────────────────── */}
        {view === 'calendar' && (
          <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className="text-3xl font-black">
                {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex gap-2 bg-white dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"><ChevronLeft size={20}/></button>
                <button onClick={() => setCurrentMonth(new Date())} className="px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-sm font-bold">Today</button>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"><ChevronRight size={20}/></button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(() => {
                const stats = getMonthStats(currentMonth.getFullYear(), currentMonth.getMonth());
                return (
                  <>
                    <StatCard label="Month Total" value={stats.total.toFixed(1)}     icon={<BarChart3 className="text-blue-500"/>}  />
                    <StatCard label="Daily Avg"   value={stats.dailyAvg.toFixed(1)}  icon={<CalendarDays className="text-amber-500"/>} />
                    <StatCard label="Weekly Avg"  value={stats.weeklyAvg.toFixed(1)} icon={<TrendingUp className="text-emerald-500"/>} />
                  </>
                );
              })()}
            </div>

            <div className="bg-white dark:bg-slate-900 p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-4">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5 sm:gap-4">
                {(() => {
                  const year     = currentMonth.getFullYear();
                  const month    = currentMonth.getMonth();       // 0-indexed
                  const firstDay = new Date(year, month, 1).getDay();
                  const days     = new Date(year, month + 1, 0).getDate();
                  const todayStr = getTodayStr();
                  const items    = [];

                  for (let i = 0; i < firstDay; i++) items.push(<div key={`empty-${i}`} />);

                  for (let d = 1; d <= days; d++) {
                    // FIX: build date string without toISOString() to avoid UTC offset bugs
                    const dateStr = buildDateStr(year, month + 1, d);
                    const count   = logs[dateStr];
                    const bgColor = getColorForCount(count === undefined ? null : count, thresholds);
                    const isToday = todayStr === dateStr;

                    items.push(
                      <button
                        key={d}
                        onClick={() => setSelectedDate({ dateStr, count: count || 0, isInitial: count === undefined })}
                        className={`aspect-square rounded-xl sm:rounded-2xl flex flex-col items-center justify-center transition-all ${isToday ? 'ring-2 sm:ring-4 ring-blue-500 shadow-lg shadow-blue-500/20 z-10 scale-105' : 'hover:scale-105'}`}
                        style={{ backgroundColor: bgColor }}
                      >
                        <span className={`text-xs sm:text-sm font-black ${bgColor !== 'transparent' ? 'text-white' : 'text-slate-400 dark:text-slate-600'}`}>{d}</span>
                        {count !== undefined && count !== 0 && (
                          <span className="text-[8px] sm:text-[10px] font-bold text-white/90">{count}</span>
                        )}
                      </button>
                    );
                  }
                  return items;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* ── Year View ─────────────────────────────────────────────── */}
        {view === 'year' && (
          <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
            <YearHeader year={displayYear} onYearChange={(d) => setDisplayYear(y => y + d)} title="Yearly Insights" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(() => {
                const current = getYearStats(displayYear);
                const prev    = getYearStats(displayYear - 1);
                const getDiff = (curr, old) => old === 0 ? null : ((curr - old) / old) * 100;
                return (
                  <>
                    <StatCard label="Year Total"   value={current.total.toFixed(1)}      diff={getDiff(current.total,       prev.total)}       />
                    <StatCard label="Daily Avg"    value={current.dailyAvg.toFixed(2)}   diff={getDiff(current.dailyAvg,    prev.dailyAvg)}    />
                    <StatCard label="Weekly Avg"   value={current.weeklyAvg.toFixed(1)}  diff={getDiff(current.weeklyAvg,   prev.weeklyAvg)}   />
                    <StatCard label="Monthly Avg"  value={current.monthlyAvg.toFixed(1)} diff={getDiff(current.monthlyAvg,  prev.monthlyAvg)}  />
                  </>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 12 }).map((_, m) => (
                <div key={m} className="bg-white dark:bg-slate-900 p-4 rounded-[1.5rem] sm:rounded-3xl border border-slate-200 dark:border-slate-800 group overflow-hidden">
                  <h3
                    className="font-black mb-3 cursor-pointer hover:text-blue-500 flex items-center justify-between"
                    onClick={() => { setCurrentMonth(new Date(displayYear, m)); setView('calendar'); }}
                  >
                    {new Date(displayYear, m).toLocaleString('default', { month: 'long' })}
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const firstDay = new Date(displayYear, m, 1).getDay();
                      const days     = new Date(displayYear, m + 1, 0).getDate();
                      const todayStr = getTodayStr();
                      const items    = [];
                      for (let i = 0; i < firstDay; i++) items.push(<div key={`e-${i}`} />);
                      for (let d = 1; d <= days; d++) {
                        // FIX: timezone-safe date string
                        const dateStr = buildDateStr(displayYear, m + 1, d);
                        const count   = logs[dateStr];
                        const bgColor = getColorForCount(count === undefined ? null : count, thresholds);
                        const isToday = todayStr === dateStr;
                        items.push(
                          <button
                            key={d}
                            onClick={() => setSelectedDate({ dateStr, count: count || 0, isInitial: count === undefined })}
                            className={`aspect-square rounded-md sm:rounded flex items-center justify-center text-[8px] font-bold transition-transform hover:scale-125 ${isToday ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-slate-900 z-10' : ''}`}
                            style={{ backgroundColor: bgColor, color: bgColor !== 'transparent' ? 'white' : 'inherit' }}
                          >
                            <span className={bgColor === 'transparent' ? 'text-slate-300 dark:text-slate-700' : ''}>{d}</span>
                          </button>
                        );
                      }
                      return items;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Advanced / Bulk-Edit View ──────────────────────────────── */}
        {view === 'advanced' && (
          <AdvancedView
            logs={logs}
            thresholds={thresholds}
            onSave={async (updates) => {
              setWriteError('');
              // FIX: validate all values before committing the batch
              for (const [, count] of Object.entries(updates)) {
                if (count !== null && (isNaN(count) || count < 0 || count > 100)) {
                  setWriteError('One or more entries are invalid. Counts must be between 0 and 100.');
                  return;
                }
              }
              try {
                const batch = writeBatch(db);
                Object.entries(updates).forEach(([dateStr, count]) => {
                  const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'daily_logs', dateStr);
                  if (count === null) batch.delete(ref);
                  else batch.set(ref, { count, updatedAt: serverTimestamp() }); // FIX: serverTimestamp
                });
                await batch.commit();
              } catch (e) {
                console.error('Batch write failed:', e);
                setWriteError('Failed to save changes. Please try again.');
              }
            }}
            displayYear={displayYear}
            onYearChange={(d) => setDisplayYear(y => y + d)}
          />
        )}

        {/* ── Settings View ─────────────────────────────────────────── */}
        {view === 'settings' && (
          <SettingsView
            thresholds={thresholds}
            timezone={timezone}
            onSave={async (t, tz) => {
              setWriteError('');
              try {
                const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'prefs');
                await setDoc(ref, { thresholds: t, timezone: tz });
                setThresholds(t); setTimezone(tz); setView('calendar');
              } catch (e) {
                console.error('Settings save failed:', e);
                setWriteError('Failed to save settings. Please try again.');
              }
            }}
            onCancel={() => setView('calendar')}
          />
        )}
      </main>

      {/* ── Day Edit Modal ──────────────────────────────────────────── */}
      {selectedDate && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-[100] animate-in fade-in zoom-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] sm:rounded-[3rem] p-6 sm:p-8 w-full max-w-sm border border-slate-200 dark:border-slate-800 shadow-2xl relative">
            {!selectedDate.isInitial && (
              <button
                onClick={() => { updateLog(selectedDate.dateStr, null); setSelectedDate(null); }}
                className="absolute top-6 sm:top-8 right-6 sm:right-8 p-2 text-slate-300 hover:text-red-500 transition-colors"
              >
                <Trash2 size={20} />
              </button>
            )}
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-black mb-1">
                {new Date(`${selectedDate.dateStr}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
              </h3>
              <p className="text-slate-500 font-medium">Log your daily units</p>
            </div>
            <div className="flex items-center justify-between gap-4 sm:gap-6 mb-8 sm:mb-10 bg-slate-50 dark:bg-slate-950 p-4 sm:p-6 rounded-[2rem]">
              <button
                onClick={() => setSelectedDate(prev => ({ ...prev, count: Math.max(0, +(prev.count - 1).toFixed(1)) }))}
                className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-slate-800 shadow rounded-xl sm:rounded-2xl flex items-center justify-center"
              >
                <Minus size={20} />
              </button>
              {/* FIX: min="0" and max="100" enforce bounds at the input level */}
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={selectedDate.count}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 100) {
                    setSelectedDate(prev => ({ ...prev, count: val }));
                  }
                }}
                className="text-4xl sm:text-5xl font-black text-blue-600 dark:text-blue-400 bg-transparent text-center w-24 sm:w-32 outline-none"
              />
              <button
                onClick={() => setSelectedDate(prev => ({ ...prev, count: Math.min(100, +(prev.count + 1).toFixed(1)) }))}
                className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 shadow rounded-xl sm:rounded-2xl flex items-center justify-center text-white"
              >
                <Plus size={20} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => setSelectedDate(null)} className="py-4 font-black text-slate-400 uppercase text-xs tracking-widest">Cancel</button>
              <button
                onClick={() => { updateLog(selectedDate.dateStr, selectedDate.count); setSelectedDate(null); }}
                className="py-4 font-black bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl uppercase text-xs tracking-widest shadow-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ label, value, icon, diff }) {
  const isPositive = diff > 0;
  return (
    <div className="bg-white dark:bg-slate-900 p-5 sm:p-6 rounded-[1.5rem] sm:rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-2">
      <div className="flex justify-between items-start">
        <span className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
        {icon || (diff !== null && diff !== undefined && (
          <div className={`flex items-center gap-0.5 text-[10px] font-black px-2 py-1 rounded-full ${isPositive ? 'bg-red-50 text-red-600 dark:bg-red-950/30' : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30'}`}>
            {isPositive ? <TrendingUp size={10}/> : <TrendingDown size={10}/>}
            {Math.abs(diff).toFixed(0)}%
          </div>
        ))}
      </div>
      <div className="text-xl sm:text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function NavBtn({ icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg sm:rounded-xl transition-all ${active ? 'bg-amber-500 text-white shadow-lg' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500'}`}
    >
      {icon}
    </button>
  );
}

function YearHeader({ year, onYearChange, title }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{title}</h2>
        <p className="text-slate-500 font-medium text-sm">Data overview for {year}</p>
      </div>
      <div className="flex gap-2 bg-white dark:bg-slate-900 p-1 rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 w-fit">
        <button onClick={() => onYearChange(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft size={20}/></button>
        <span className="px-4 py-2 text-sm font-black flex items-center">{year}</span>
        <button onClick={() => onYearChange(1)}  className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight size={20}/></button>
      </div>
    </div>
  );
}

function LoginView({ onAuth, error }) {
  const [isSignUp,  setIsSignUp]  = useState(false);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md space-y-8 bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-2xl">
        <div className="text-center">
          <div className="inline-flex p-4 sm:p-5 bg-amber-500/10 rounded-3xl mb-6"><Beer className="text-amber-500" size={40}/></div>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white">{isSignUp ? 'New Account' : 'Welcome Back'}</h2>
        </div>
        <div className="space-y-4">
          <input type="email"    placeholder="Email"    value={email}    onChange={e => setEmail(e.target.value)}    className="w-full bg-slate-50 dark:bg-slate-950 border rounded-2xl py-4 px-4 outline-none focus:ring-2 ring-blue-500" />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border rounded-2xl py-4 px-4 outline-none focus:ring-2 ring-blue-500" />
        </div>
        {error && <div className="text-red-500 text-xs font-bold bg-red-50 p-4 rounded-xl">{error}</div>}
        <button
          onClick={() => onAuth(email, password, isSignUp)}
          className="w-full py-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black rounded-2xl uppercase text-xs tracking-widest shadow-xl"
        >
          {isSignUp ? 'Create Account' : 'Sign In'}
        </button>
        <p className="text-center text-slate-400 text-sm">
          {isSignUp ? 'Member already?' : 'New here?'}
          <button onClick={() => setIsSignUp(!isSignUp)} className="text-blue-600 font-black ml-1">{isSignUp ? 'Sign In' : 'Sign Up'}</button>
        </p>
      </div>
    </div>
  );
}

function SettingsView({ thresholds, timezone, onSave, onCancel }) {
  const [localThresholds, setLocalThresholds] = useState(thresholds);
  const [localTimezone,   setLocalTimezone]   = useState(timezone);

  return (
    <div className="space-y-8 max-w-2xl mx-auto animate-in slide-in-from-right-4 duration-500">
      <h2 className="text-3xl font-black">App Settings</h2>
      <div className="bg-white dark:bg-slate-900 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-slate-200 dark:border-slate-800 space-y-8">
        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Daily Reset Timezone</label>
          <select
            value={localTimezone}
            onChange={e => setLocalTimezone(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl py-4 px-4 font-bold outline-none focus:ring-2 ring-blue-500"
          >
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center ml-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Shading Rules</label>
            <button
              onClick={() => setLocalThresholds([...localThresholds, { max: 10, color: '#3b82f6', label: 'New' }])}
              className="text-blue-500 font-black text-xs hover:underline"
            >
              + Add Level
            </button>
          </div>
          <div className="space-y-4">
            {localThresholds.map((t, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-4 sm:items-center bg-slate-50 dark:bg-slate-950 p-5 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="flex-1 min-w-0">
                  <label className="text-[8px] font-black text-slate-400 uppercase mb-1 block">Label</label>
                  <input
                    value={t.label}
                    onChange={e => { const n = [...localThresholds]; n[i] = { ...n[i], label: e.target.value }; setLocalThresholds(n); }}
                    className="w-full bg-transparent font-bold outline-none text-sm border-b border-slate-200 dark:border-slate-700 pb-1 focus:border-blue-500"
                    placeholder="Label"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24">
                    <label className="text-[8px] font-black text-slate-400 uppercase mb-1 block text-right">Max</label>
                    <input
                      type="number" step="0.1" min="0" max="100"
                      value={t.max}
                      onChange={e => { const n = [...localThresholds]; n[i] = { ...n[i], max: parseFloat(e.target.value) || 0 }; setLocalThresholds(n); }}
                      className="w-full bg-transparent font-bold outline-none text-sm text-right border-b border-slate-200 dark:border-slate-700 pb-1 focus:border-blue-500"
                    />
                  </div>
                  <input
                    type="color"
                    value={t.color}
                    onChange={e => { const n = [...localThresholds]; n[i] = { ...n[i], color: e.target.value }; setLocalThresholds(n); }}
                    className="w-10 h-10 rounded-xl cursor-pointer bg-white dark:bg-slate-800 p-0.5 border border-slate-200 dark:border-slate-700"
                  />
                  <button onClick={() => setLocalThresholds(localThresholds.filter((_, idx) => idx !== i))} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 size={18}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 pb-10">
        <button onClick={onCancel} className="flex-1 py-5 font-black text-slate-400 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl sm:rounded-[2rem] uppercase text-xs tracking-widest">Discard</button>
        <button onClick={() => onSave(localThresholds, localTimezone)} className="flex-1 py-5 font-black bg-blue-600 text-white rounded-2xl sm:rounded-[2rem] shadow-xl uppercase text-xs tracking-widest">Save Changes</button>
      </div>
    </div>
  );
}

function AdvancedView({ logs, thresholds, onSave, displayYear, onYearChange }) {
  const [searchTerm,    setSearchTerm]    = useState('');
  const [localUpdates,  setLocalUpdates]  = useState({});
  const [saving,        setSaving]        = useState(false);
  // FIX: surface per-row validation errors
  const [validationErr, setValidationErr] = useState('');

  const filteredDates = useMemo(() => {
    const dates = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(displayYear, m + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const date    = new Date(displayYear, m, d);
        const dateStr = date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', weekday: 'long' }).toLowerCase();
        if (dateStr.includes(searchTerm.toLowerCase())) dates.push(date);
      }
    }
    return dates;
  }, [displayYear, searchTerm]);

  const handleCommit = async () => {
    setValidationErr('');
    for (const [, count] of Object.entries(localUpdates)) {
      if (count !== null && (isNaN(count) || count < 0 || count > 100)) {
        setValidationErr('Invalid entry detected. All counts must be between 0 and 100.');
        return;
      }
    }
    setSaving(true);
    await onSave(localUpdates);
    setLocalUpdates({});
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <YearHeader year={displayYear} onYearChange={onYearChange} title="Bulk Editor" />
        <button
          onClick={handleCommit}
          disabled={Object.keys(localUpdates).length === 0 || saving}
          className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest disabled:opacity-50 shadow-lg"
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
      {validationErr && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-bold rounded-2xl">{validationErr}</div>
      )}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18}/>
        <input
          type="text"
          placeholder="Search dates..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border rounded-2xl py-4 pl-12 pr-4 font-bold outline-none shadow-sm focus:ring-2 ring-blue-500"
        />
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-[2rem] border overflow-hidden shadow-xl">
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10 border-b">
              <tr>
                <th className="p-4 text-left font-black text-slate-400 uppercase tracking-widest text-[10px]">Date</th>
                <th className="p-4 text-right font-black text-slate-400 uppercase tracking-widest text-[10px] w-32">Units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredDates.map(date => {
                // FIX: build date string without toISOString() UTC offset risk
                const dateId     = buildDateStr(date.getFullYear(), date.getMonth() + 1, date.getDate());
                const currentVal = logs[dateId];
                const localVal   = localUpdates[dateId];
                const displayVal = localVal !== undefined ? (localVal === null ? '' : localVal) : (currentVal === undefined ? '' : currentVal);
                const countNum   = localVal !== undefined ? (localVal === null ? 0 : localVal) : (currentVal === undefined ? 0 : currentVal);
                const color      = getColorForCount(countNum, thresholds);
                const hasValue   = localVal !== undefined || currentVal !== undefined;

                return (
                  <tr
                    key={dateId}
                    className="transition-all duration-300"
                    style={{
                      backgroundColor: hasValue && color !== 'transparent' ? `${color}1A` : 'transparent',
                      borderLeft: hasValue && color !== 'transparent' ? `6px solid ${color}` : '6px solid transparent'
                    }}
                  >
                    <td className="p-4 font-bold pl-3">
                      {date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </td>
                    <td className="p-4 text-right">
                      {/* FIX: min/max attributes enforce bounds in the bulk editor too */}
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={displayVal}
                        onChange={e => setLocalUpdates(p => ({
                          ...p,
                          [dateId]: e.target.value === '' ? null : parseFloat(e.target.value)
                        }))}
                        className="w-20 bg-slate-100 dark:bg-slate-800/50 rounded-lg px-3 py-2 text-right font-black outline-none border-2 border-transparent focus:border-blue-500 shadow-inner"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-center p-4">
        <p className="text-slate-400 text-xs font-medium italic">Changes are staged locally. Click "Save All" to commit to database.</p>
      </div>
    </div>
  );
}
